// a collection used to contain information about the user who was sent the questionnaire, the user it was sent to, and information about the response.

import base from "@/fstore";
import validation from "@/fstore/validation";
import helper from '@/fstore/helpers';
import moment from 'moment';

const db = base.db;
const collection = "screening";
const model = db.collection(collection);

const template = {
  jobRef: {
    type: "object",
    required: true
  },
  companyRef: {
    type: "object",
    required: true
  },
  userRef: {
    type: "object",
    required: true,
    description: "The reference to the user the screening is being sent to"
  },
  received: {
    type: 'object',
    default: new Date(),
    description: "The date this was sent to the candidate"
  },
  dateDeleted: {
    type: 'object',
    default: null,
    description: "The date this screening was deleted or 'resolved'"
  },
  expires: {
    type: 'object',
    default: moment(new Date()).add(5, 'days').toDate(),
    description: 'The date the screening will be considered expired.'
  },
  sentBy: {
    type: 'object',
    required: true,
    description: "The user who sent this out to the candidate"
  },
  questionnaire: {
    type: "object",
    required: true
  },
  connectionRequest:{
    type: 'object',
    default: {
      sent: false,
      accepted: false,
      rejected: false,
      downloadCount: 0
    }
  },
  status: {
    type: 'object',
    default: {
      opened: false,
      submitted: false,
      rejected: false,
    }
  },
  rejection: {
    type: 'object',
    default: {
      rejected_by: "", // will be one of Candidate, or Company
      rejected_reason: "",
      rejected_date: null,
      companyAcknowledged: false, // will be auto set when company rejects, needs to be manually acknowledged if candidate rejects.
    }
  }
}

const _exists = async (screeningToValidate) => {
  //get screenings for job
  const screenings = await fetchScreeningsForJob(screeningToValidate.jobRef.id);

  let found = false;

  screenings.forEach(screening => {
    if(screening.userRef.id === screeningToValidate.userRef.id)
      found = true;
  });

  return found;
}

const create = async (screening) => {
  //verify if the screening has been sent to the user before proceeding
  if(await _exists(screening))
    return {doc:screening, errors:['This candidate has already been sent a screening for this position.']};

  // convert ids to reference objects if ids were provided
  if(typeof(screening.companyRef) == 'string')
    screening.companyRef = await db.doc(`company/${screening.companyRef}`);
  
  if(typeof(screening.jobRef) == 'string')
    screening.jobRef = await db.doc(`job/${screening.jobRef}`);
  
  if(typeof(screening.userRef) == 'string')
    screening.userRef = await db.doc(`userdata/${screening.userRef}`);

  // validation
  const response = await validation.validateModel(model, screening, template);
  if (!response.errors.length) {
    const newDoc = await model.add(response.doc);
    response.doc.uid = newDoc.id
  }

  return response;
}

const update = async (screening) => {
  try {
    await model.doc(screening.uid).set(screening, {merge: true});
    return true;
  }catch(err) {
    console.error(err);
    return false;
  }
}

const updateBatch = async (screenings) => {
  try {
    if(!screenings.length)
      return;

    for(const screening of screenings) {
      await update(screening);
    }
  }catch(err) {
    console.error(err);
    return false;
  }
}


const remove = async (screeningId) => {
  throw new Error('Not Implemented Yet');
}

const removeScreeningsForJob = async (jobId) => {
  const screenings = await fetchScreeningsForJob(jobId);

  screenings.forEach(screening => {
    screening.dateDeleted = new Date()
  });

  await updateBatch(screenings);
}

const fetchScreeningsForUser = async (userId) => {
  const today = new Date();
  const userRef = await db.doc(`userdata/${userId}`);
  const refs = await model.where("userRef", "==", userRef).where("dateDeleted", "==", null).get();
  const screenings = helper.getDataForRefs(refs);

  return screenings.sort((a,b) => {
    if(a.received < b.received) return 1;
    if(a.received > b.received) return -1;

    return 0;
  });
}

const fetchScreeningsForJob = async (jobId) => {
  const jobRef = await db.doc(`job/${jobId}`);
  const refs = await model.where("jobRef", "==", jobRef).where("dateDeleted", "==", null).get();
  const screenings = helper.getDataForRefs(refs);

  const getDataForRef = async ref => {
    return (await ref.get()).data();
  }

  screenings.forEach(screening => {
    getDataForRef(screening.userRef).then(data => {
      screening.candidate = data;

      getDataForRef(screening.jobRef).then(data => {
        screening.job = data;

        let skillsMatched = 0

        screening.candidate.skills.forEach(skill => {
          screening.job.skills.forEach(jobSkill => {
            if(jobSkill.name == skill.name) ++skillsMatched;
          });
        });

        screening.candidate.skillsMatched = skillsMatched;

        // check the location
        const userIsLocal = screening.candidate.cities.some(location => location.city === screening.job.location.city);
        screening.candidate.location = userIsLocal? screening.job.location.city : 'Remote';
      });
    });
  });

  screenings.sort((a, b) => {
      // true values first
      return (a.status.submitted === b.status.submitted)? 0 : a? 1 : -1;
      // false values first
      // return (a.status.submitted === b.status.submitted)? 0 : x? -1 : 1;
  });

  return screenings;
}

const fetchScreeningsForCompany = async (companyId) => {
  throw new Error('Not Implemented Yet');
}

const getTemplate = () => {
  return helper.getTemplate(template);
}

export default {
  create,
  update,
  updateBatch,
  remove,
  removeScreeningsForJob,
  fetchScreeningsForUser,
  fetchScreeningsForJob,
  fetchScreeningsForCompany,
  getTemplate
}